import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Folder, File, ChevronRight, ArrowLeft, Plus, Edit2, Trash2, Upload, Download } from 'lucide-react';
import { useDocumentation } from '../context/DocumentationContext';
import styles from './DocumentationBrowser.module.css';

const DocumentationBrowser = () => {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const { cards, updateCardFolders } = useDocumentation();
  
  const card = cards[cardId];
  const [currentPath, setCurrentPath] = useState(['root']);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createType, setCreateType] = useState(null);
  const [newItemName, setNewItemName] = useState('');
  const [editingItem, setEditingItem] = useState(null);

  if (!card) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2 style={{ color: '#1e293b' }}>Card not found</h2>
        <button
          onClick={() => navigate('/documentation')}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Back to Documentation
        </button>
      </div>
    );
  }

  const getCurrentFolder = () => {
    let current = card.folders.root;
    for (let i = 1; i < currentPath.length; i++) {
      current = current.children[currentPath[i]];
    }
    return current?.children || {};
  };

  const createItem = () => {
    if (!newItemName.trim()) return;

    const newFolders = JSON.parse(JSON.stringify(card.folders));
    let current = newFolders.root;
    
    for (let i = 1; i < currentPath.length; i++) {
      current = current.children[currentPath[i]];
    }

    if (createType === 'folder') {
      current.children[newItemName] = {
        type: 'folder',
        children: {}
      };
    }

    updateCardFolders(cardId, newFolders);
    setIsCreating(false);
    setCreateType(null);
    setNewItemName('');
  };

  const deleteItem = (itemName) => {
    const newFolders = JSON.parse(JSON.stringify(card.folders));
    let current = newFolders.root;
    
    for (let i = 1; i < currentPath.length; i++) {
      current = current.children[currentPath[i]];
    }

    delete current.children[itemName];
    updateCardFolders(cardId, newFolders);
  };

  const renameItem = (oldName, newName) => {
    if (!newName.trim()) return;

    const newFolders = JSON.parse(JSON.stringify(card.folders));
    let current = newFolders.root;
    
    for (let i = 1; i < currentPath.length; i++) {
      current = current.children[currentPath[i]];
    }

    current.children[newName] = current.children[oldName];
    delete current.children[oldName];
    updateCardFolders(cardId, newFolders);
    setEditingItem(null);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    const newFolders = JSON.parse(JSON.stringify(card.folders));
    let current = newFolders.root;
    
    for (let i = 1; i < currentPath.length; i++) {
      current = current.children[currentPath[i]];
    }

    files.forEach(file => {
      const fileData = {
        type: 'uploaded-file',
        name: file.name,
        size: file.size,
        fileType: file.type,
        created: new Date().toLocaleString(),
        url: URL.createObjectURL(file)
      };
      
      current.children[file.name] = fileData;
    });

    updateCardFolders(cardId, newFolders);
  };

  const navigateToFolder = (folderName) => {
    setCurrentPath([...currentPath, folderName]);
  };

  const goBack = () => {
    if (currentPath.length > 1) {
      setCurrentPath(currentPath.slice(0, -1));
    }
  };

  const openFile = (itemName, item) => {
    const pathString = currentPath.slice(1).join('/');
    const filePath = pathString ? `${pathString}/${itemName}` : itemName;
    navigate(`/documentation/${cardId}/file/${encodeURIComponent(filePath)}?mode=view`);
  };

  const downloadFile = (itemName, item) => {
    if (item.type === 'uploaded-file') {
      const link = document.createElement('a');
      link.href = item.url;
      link.download = itemName;
      link.click();
      return;
    }

    if (item.type === 'file') {
      const blob = new Blob([item.content || ''], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${itemName}.html`;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const downloadCurrentFolder = () => {
    const current = currentPath.join('/');
    const payload = {
      path: currentPath,
      folders: getCurrentFolder()
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${current || 'root'}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const createNewFile = () => {
    const pathString = currentPath.slice(1).join('/');
    navigate(`/documentation/${cardId}/new${pathString ? `?path=${encodeURIComponent(pathString)}` : ''}`);
  };

  const currentContents = getCurrentFolder();
  const items = Object.entries(currentContents);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => navigate('/documentation')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: '#64748b',
              fontSize: '14px',
              gap: '6px'
            }}
          >
            <ArrowLeft size={20} />
          </button>
          <Folder size={28} color="#f59e0b" />
          <h1 style={{ margin: 0, color: '#1e293b', fontSize: '24px' }}>{card.name}</h1>
        </div>
        <button
          onClick={() => setIsEditMode(!isEditMode)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 20px',
            background: isEditMode ? '#dbeafe' : '#f1f5f9',
            color: isEditMode ? '#1e40af' : '#64748b',
            border: isEditMode ? '1px solid #3b82f6' : '1px solid #e2e8f0',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          <Edit2 size={18} />
          {isEditMode ? 'Exit Edit Mode' : 'Edit Mode'}
        </button>
      </div>

      <div className={styles.breadcrumb}>
        {currentPath.map((pathPart, idx) => (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {idx > 0 && <ChevronRight size={16} color="#94a3b8" />}
            <button
              onClick={() => setCurrentPath(currentPath.slice(0, idx + 1))}
              style={{
                background: 'none',
                border: 'none',
                color: idx === currentPath.length - 1 ? '#1e293b' : '#64748b',
                cursor: 'pointer',
                textDecoration: idx === currentPath.length - 1 ? 'none' : 'underline',
                fontSize: '14px'
              }}
            >
              {pathPart === 'root' ? 'Root' : pathPart}
            </button>
          </div>
        ))}
      </div>

      <div className={styles.toolbar}>
        {isEditMode && (
          <>
            <button onClick={() => { setCreateType('folder'); setIsCreating(true); }} className={styles.btnPrimary}>
              <Plus size={16} /> New Folder
            </button>
            <button onClick={createNewFile} className={styles.btnPrimary}>
              <Plus size={16} /> New File
            </button>
            <label className={styles.btnSecondary} style={{ margin: 0 }}>
              <input type="file" multiple onChange={handleFileUpload} style={{ display: 'none' }} />
              <Upload size={16} /> Upload Files
            </label>
            {currentPath.length > 1 && (
              <button onClick={goBack} className={styles.btnSecondary}>
                <ArrowLeft size={16} /> Back
              </button>
            )}
          </>
        )}
        <button onClick={downloadCurrentFolder} className={styles.btnSecondary}>
          <Download size={16} /> Download Folder
        </button>
      </div>

      <div className={styles.fileList}>
        {items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
            <Folder size={64} color="#cbd5e1" style={{ marginBottom: '20px' }} />
            <p style={{ fontSize: '16px', marginBottom: '10px' }}>This folder is empty</p>
            <p style={{ fontSize: '14px' }}>Create a folder or file to get started</p>
          </div>
        ) : (
          items.map(([itemName, item]) => (
            <div key={itemName} className={styles.fileItem}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, cursor: 'pointer' }}
                onClick={() => {
                  if (item.type === 'folder') {
                    navigateToFolder(itemName);
                  } else {
                    openFile(itemName, item);
                  }
                }}
              >
                {item.type === 'folder' ? (
                  <>
                    <Folder size={24} color="#f59e0b" />
                    <span style={{ fontWeight: '500', color: '#1e293b', fontSize: '15px' }}>{itemName}</span>
                  </>
                ) : item.type === 'uploaded-file' ? (
                  <>
                    <File size={24} color="#8b5cf6" />
                    <div>
                      <div style={{ fontWeight: '500', color: '#1e293b', fontSize: '15px' }}>{itemName}</div>
                      <div style={{ color: '#94a3b8', fontSize: '12px' }}>
                        {(item.size / 1024).toFixed(2)} KB · {item.created}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <File size={24} color="#3b82f6" />
                    <div>
                      <div style={{ fontWeight: '500', color: '#1e293b', fontSize: '15px' }}>{itemName}</div>
                      <div style={{ color: '#94a3b8', fontSize: '12px' }}>{item.created}</div>
                    </div>
                  </>
                )}
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); downloadFile(itemName, item); }}
                  className={styles.iconBtn}
                  title="Download"
                >
                  <Download size={16} />
                </button>
                {isEditMode && (
                  <>
                    {editingItem === itemName ? (
                      <input
                        autoFocus
                        defaultValue={itemName}
                        onBlur={() => setEditingItem(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            renameItem(itemName, e.target.value);
                          } else if (e.key === 'Escape') {
                            setEditingItem(null);
                          }
                        }}
                        style={{
                          padding: '6px 10px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                    ) : (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingItem(itemName); }}
                          className={styles.iconBtn}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteItem(itemName); }}
                          className={styles.iconBtnDanger}
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {isCreating && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3 style={{ margin: '0 0 20px 0', color: '#1e293b' }}>
              Create New {createType === 'folder' ? 'Folder' : 'File'}
            </h3>
            <input
              autoFocus
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              placeholder={`Enter ${createType} name`}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  createItem();
                }
              }}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                marginBottom: '20px'
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setIsCreating(false); setNewItemName(''); }}
                style={{
                  padding: '10px 20px',
                  background: 'none',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  color: '#64748b',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={createItem}
                disabled={!newItemName.trim()}
                style={{
                  padding: '10px 20px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: newItemName.trim() ? 'pointer' : 'not-allowed',
                  opacity: newItemName.trim() ? 1 : 0.5
                }}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentationBrowser;
